import React from "react";
import { useSelector } from "react-redux";

import {
  getUserPreferenceValue,
  selectCurrentAccount,
  selectCurrentRole,
  useGetAccountsQuery,
  useGetExternalAccountsQuery,
  useGetUserMeAccountsQuery,
  useGetUserMeQuery,
  useGetUserPreferencesQuery,
  useUpdateUserPreferencesMutation,
} from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { useMfaStatus } from "fond/hooks/useMfaStatus";
import { useSwitchableAccounts } from "fond/hooks/useSwitchableAccounts";
import TermsofServiceChange from "fond/terms/TermsofServiceChange/TermsofServiceChange";
import { SelfServiceTour } from "fond/tours";
import { MFA_NONE, Store, UserPreferenceKey, UserPreferenceSubKey, UserRole, UserRoles } from "fond/types";
import SelectAccount from "fond/views/Accounts/SelectAccount";
import NoMatch from "fond/views/Error/NoMatch";
import MFASetupRequired from "fond/views/MFASetupRequired/MFASetupRequired";
import { BlockSpinner, ErrorModal } from "fond/widgets";

export default function withUserInfo<T extends object>(
  WrappedComponent: React.ComponentType<T>,
  minimumRole: (typeof UserRoles)[UserRole] = 0
): (props: T) => React.ReactElement<T> {
  return (props: T) => {
    const {
      isLoading: isLoadingUserPreferences,
      isError: isErrorUserPreferences,
      refetch: refetchUserPreferences,
    } = useGetUserPreferencesQuery(undefined);
    const { isLoading: isLoadingUser, isError: isErrorUser, refetch: refetchUser } = useGetUserMeQuery();
    const { isLoading: isLoadingUserAccounts, isError: isErrorUserAccounts, refetch: refetchUserAccounts } = useGetUserMeAccountsQuery();
    const { isLoading: isLoadingAccounts, isError: isErrorAccounts, refetch: refetchAccounts } = useGetAccountsQuery();
    const {
      isLoading: isLoadingExternalAccounts,
      isError: isErrorExternalAccounts,
      refetch: refetchExternalAccounts,
    } = useGetExternalAccountsQuery();
    const [updatePreference] = useUpdateUserPreferencesMutation();
    const { accounts, isLoading: isLoadingSwitchableAccounts } = useSwitchableAccounts();
    const role = useSelector(selectCurrentRole);
    const account = useSelector(selectCurrentAccount);
    const { value: isMfaEnabled, loading: isLoadingMfaFlag } = useFeatureFlag("security_mfa", false);
    const accountRequiresMfa = isMfaEnabled && account && "Mfa" in account && account.Mfa;
    const { mfaStatus, isLoading: isLoadingMfaStatus, error: mfaError, refresh: refreshMfaStatus } = useMfaStatus();
    const currentAccountId = useSelector((state: Store) => state.accountContext?.currentAccountId);
    const noAccountSelected = useSelector((state: Store) => {
      return state.accountContext?.isInitialized && !state.accountContext?.currentAccountId;
    });
    const switchingAccounts = useSelector((state: Store) => {
      return state.accountContext?.isSwitchingAccount;
    });

    // Check if user has an account ID set but it's not in their available accounts (invalid account context)
    const hasInvalidAccount = currentAccountId && !isLoadingSwitchableAccounts && !accounts.some((acc) => acc.ID === currentAccountId);
    const showTermsofServiceChange = useSelector((state: Store) =>
      getUserPreferenceValue(state, UserPreferenceKey.TOS_CHANGE, UserPreferenceSubKey.SEP_2025_TOS_CHANGE)
    );
    const showSelfServiceTour = useSelector((state: Store) =>
      getUserPreferenceValue(state, UserPreferenceKey.UI_TOUR, UserPreferenceSubKey.SELF_SERVICE_TOUR)
    );

    const onDismiss = () => {
      if (isErrorAccounts) refetchAccounts();

      if (isErrorExternalAccounts) refetchExternalAccounts();

      if (isErrorUser) refetchUser();

      if (isErrorUserAccounts) refetchUserAccounts();

      if (isErrorUserPreferences) refetchUserPreferences();

      if (mfaError) refreshMfaStatus();
    };

    const getComponentWithUserInfo = () => {
      switch (true) {
        case isErrorUserPreferences || isErrorUser || isErrorUserAccounts || isErrorAccounts || isErrorExternalAccounts:
          return <ErrorModal onClose={onDismiss} />;
        case isLoadingUserPreferences ||
          isLoadingUser ||
          isLoadingUserAccounts ||
          isLoadingAccounts ||
          isLoadingExternalAccounts ||
          isLoadingMfaFlag ||
          switchingAccounts:
          return <BlockSpinner containerProps={{ height: "100%" }} />;
        case noAccountSelected && accounts?.length > 0:
        case hasInvalidAccount:
          return <SelectAccount />;
        case UserRoles[role ?? "member"] < minimumRole:
          return <NoMatch />;
        case accountRequiresMfa && isLoadingMfaStatus:
          return <BlockSpinner containerProps={{ height: "100%" }} />;
        case accountRequiresMfa && !!mfaError:
          return <ErrorModal onClose={onDismiss} />;
        case accountRequiresMfa && mfaStatus === MFA_NONE:
          return <MFASetupRequired onComplete={refreshMfaStatus} />;
        default:
          // This is to avoid overriding the wrapped components `onLoad`. For more info see:
          // https://bitbucket.org/biarrinetworks/fond_ui/pull-requests/222/mag-1190-add-feature-set-enforcement-to/diff#Lsrc/fond/accounts/withUserInfo.jsT29
          return (
            <>
              {showTermsofServiceChange && <TermsofServiceChange />}
              {showSelfServiceTour && (
                <SelfServiceTour
                  onComplete={() => {
                    updatePreference({ Key: UserPreferenceKey.UI_TOUR, Subkey: UserPreferenceSubKey.SELF_SERVICE_TOUR, Value: false });
                  }}
                />
              )}
              <WrappedComponent {...props} />
            </>
          );
      }
    };
    const ComponentWithUserInfo = getComponentWithUserInfo();

    return ComponentWithUserInfo;
  };
}
